'use client';

import { bootSession } from 'AuthBff/session-boot';
import type { AuthState, AuthUser, TenantSummary } from 'AuthBff/types';
import { createContext, type ReactNode, useCallback, useEffect, useState } from 'react';

export type AuthContextValue = {
  state: AuthState;
  hydrate: (data: { accessToken: string; idToken?: string | null; user: AuthUser; activeTenant: TenantSummary | null }) => void;
  setUnauthenticated: () => void;
  setAccessToken: (accessToken: string, activeTenant: TenantSummary | null) => void;
  setIdToken: (idToken: string) => void;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children, oidc = false, meUrl = '/api/auth/me', silentSso = true, navigate }: { children: ReactNode; oidc?: boolean; meUrl?: string; silentSso?: boolean; navigate?: (path: string) => void }) => {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const hydrate = useCallback((data: { accessToken: string; idToken?: string | null; user: AuthUser; activeTenant: TenantSummary | null }) => {
    // Preserve an existing idToken when a caller omits it (settings/billing pages
    // re-hydrate on token refresh without threading the idToken) — only an explicit
    // value (including null) overwrites it.
    setState(prev => ({ accessToken: data.accessToken, activeTenant: data.activeTenant, idToken: data.idToken !== undefined ? data.idToken : prev.status === 'authenticated' ? prev.idToken : null, status: 'authenticated', user: data.user }));
  }, []);

  const setUnauthenticated = useCallback(() => setState({ status: 'unauthenticated' }), []);

  const setAccessToken = useCallback((accessToken: string, activeTenant: TenantSummary | null) => {
    setState(prev => (prev.status === 'authenticated' ? { ...prev, accessToken, activeTenant } : prev));
  }, []);

  const setIdToken = useCallback((idToken: string) => {
    setState(prev => (prev.status === 'authenticated' ? { ...prev, idToken } : prev));
  }, []);

  useEffect(() => {
    let cancelled = false;
    void bootSession({ isCancelled: () => cancelled, meUrl, navigate, oidc, silentSso }).then(next => {
      if (cancelled) return;
      if (next.status === 'authenticated') hydrate(next);
      if (next.status === 'unauthenticated') setUnauthenticated();
    });
    return () => {
      cancelled = true;
    };
  }, [hydrate, setUnauthenticated, oidc, meUrl, navigate, silentSso]);

  return <AuthContext.Provider value={{ hydrate, setAccessToken, setIdToken, setUnauthenticated, state }}>{children}</AuthContext.Provider>;
};
